/**
 * Buscador de la cabecera del panel — bloque 1 (2026-09-18).
 *
 * Busca a la vez en clientes, productos y presupuestos. Los datos se piden la
 * primera vez que se escribe (no al abrir el panel) y se filtran aquí: con los
 * límites de FREE_V1 son unas decenas de filas.
 *
 * No es un combo (`OD-0017 D-01`): es un campo de texto y, debajo, una lista
 * de botones normales. Tabulador y lectores de pantalla funcionan sin nada
 * especial, y el número de resultados se anuncia con `aria-live`.
 */
import { useEffect, useRef, useState } from "react";
import { api, type Cliente, type Producto, type ResumenFactura, type ResumenPresupuesto } from "../api/cliente";
import { t } from "../i18n/es";
import { Icono } from "../ui/Iconos";

type Resultado = { readonly tipo: "cliente" | "producto" | "presupuesto" | "factura"; readonly id: string; readonly texto: string };

const MAXIMO = 8;
const MINIMO = 2;

export function BuscadorGlobal({
  onAbrirCliente,
  onAbrirProducto,
  onAbrirPresupuesto,
  onAbrirFactura,
}: {
  onAbrirCliente: (id: string) => void;
  onAbrirProducto: (id: string) => void;
  onAbrirPresupuesto: (id: string) => void;
  onAbrirFactura: (id: string) => void;
}) {
  const [texto, setTexto] = useState("");
  const [datos, setDatos] = useState<{
    clientes: readonly Cliente[];
    productos: readonly Producto[];
    presupuestos: readonly ResumenPresupuesto[];
    facturas: readonly ResumenFactura[];
  } | null>(null);
  const [cargando, setCargando] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  // Ctrl+K / Cmd+K lleva el foco aquí desde cualquier pantalla del panel. Es el
  // atajo que ya espera quien usa otras aplicaciones; sin él hay que ir con el
  // ratón a la barra de arriba.
  useEffect(() => {
    const tecla = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        campo.current?.focus();
      }
    };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, []);

  async function cargar(): Promise<void> {
    if (datos !== null || cargando) return;
    setCargando(true);
    const [c, p, q, f] = await Promise.all([
      api.listarClientes("ACTIVE"),
      api.listarProductos(),
      api.listarPresupuestos(null),
      api.listarFacturas(null),
    ]);
    setDatos({
      clientes: c.ok ? c.datos : [],
      productos: p.ok ? p.datos : [],
      presupuestos: q.ok ? q.datos : [],
      facturas: f.ok ? f.datos : [],
    });
    setCargando(false);
  }

  const buscado = texto.trim().toLowerCase();
  const incluye = (...campos: ReadonlyArray<string | null>): boolean => campos.some((c) => c !== null && c.toLowerCase().includes(buscado));
  const resultados: Resultado[] =
    datos === null || buscado.length < MINIMO
      ? []
      : [
          ...datos.clientes
            .filter((c) => incluye(c.displayName, c.taxIdNormalized))
            .map((c) => ({ tipo: "cliente" as const, id: c.id, texto: c.displayName })),
          ...datos.productos
            .filter((p) => incluye(p.code, p.description))
            .map((p) => ({ tipo: "producto" as const, id: p.id, texto: `${p.code} · ${p.description}` })),
          ...datos.presupuestos
            .filter((q) => incluye(q.number, q.customerName))
            .map((q) => ({ tipo: "presupuesto" as const, id: q.id, texto: `${q.number} · ${q.customerName ?? ""}` })),
          ...datos.facturas
            .filter((f) => incluye(f.number, f.customerName))
            .map((f) => ({
              tipo: "factura" as const,
              id: f.id,
              texto: `${f.number ?? t.facturas.sinNumero} · ${f.customerName ?? ""}`,
            })),
        ].slice(0, MAXIMO);

  function abrir(r: Resultado): void {
    setTexto("");
    if (r.tipo === "cliente") onAbrirCliente(r.id);
    else if (r.tipo === "producto") onAbrirProducto(r.id);
    else if (r.tipo === "factura") onAbrirFactura(r.id);
    else onAbrirPresupuesto(r.id);
  }

  return (
    <div className="buscador-global" role="search">
      <label htmlFor="buscador-global" className="solo-lector">
        {t.buscador.etiqueta}
      </label>
      <span className="buscador-global-icono">
        <Icono nombre="buscar" />
      </span>
      <input
        id="buscador-global"
        ref={campo}
        type="search"
        value={texto}
        placeholder={t.buscador.marcador}
        autoComplete="off"
        onFocus={() => void cargar()}
        onChange={(e) => {
          setTexto(e.target.value);
          void cargar();
        }}
        onKeyDown={(e) => {
          // Escape limpia y suelta el foco: salir de una búsqueda a medias no
          // puede obligar a borrar a mano lo escrito.
          if (e.key === "Escape") {
            setTexto("");
            e.currentTarget.blur();
          }
        }}
      />
      <span className="buscador-global-atajo" aria-hidden="true">
        {t.buscador.atajo}
      </span>
      {buscado.length >= MINIMO ? (
        <div className="buscador-global-resultados">
          <p className="solo-lector" aria-live="polite">
            {cargando ? t.buscador.cargando : t.buscador.encontrados(resultados.length)}
          </p>
          {!cargando && resultados.length === 0 ? <p className="ayuda-neutra">{t.buscador.sinResultados}</p> : null}
          <ul>
            {resultados.map((r) => (
              <li key={`${r.tipo}-${r.id}`}>
                <button type="button" onClick={() => abrir(r)}>
                  <span className="buscador-global-tipo">{t.buscador.tipos[r.tipo]}</span>
                  {r.texto}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
